import React, { useState } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Loader2, PlayCircle, RefreshCw, StopCircle } from "lucide-react"
import { toast } from "sonner"

import { stagesConfig } from "@/components/job-postings/KanbanStageConfig"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Progress } from "@/components/ui/progress"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import DashboardLayout from "@/layouts/DashboardLayout"
import { supabase } from "@/lib/supabase"

interface Application {
  id: string
  application_id: string | null
  applicant: {
    first_name: string
    last_name: string
    driving_licence_number: string
  }
  current_stage: string
  status: string
  created_at: string
  last_dvla_check?: {
    created_at: string
  } | null
}

interface RecheckResult {
  applicationId: string
  success: boolean
  error?: string
}

const DVLARecheck: React.FC = () => {
  const queryClient = useQueryClient()
  const [selectedApplications, setSelectedApplications] = useState<string[]>([])
  const [intervalSeconds, setIntervalSeconds] = useState(5)
  const [isProcessing, setIsProcessing] = useState(false)
  const [shouldStop, setShouldStop] = useState(false)
  const [progress, setProgress] = useState(0)
  const [results, setResults] = useState<RecheckResult[]>([])
  const [stageFilter, setStageFilter] = useState<string>("complete")

  // Fetch applications by stage
  const { data: applications, isLoading } = useQuery({
    queryKey: ["applications-by-stage", stageFilter],
    queryFn: async () => {
      let query = supabase
        .from("applications")
        .select(
          `
          id,
          application_id,
          current_stage,
          status,
          created_at,
          applicant:applicant_id(
            first_name,
            last_name,
            driving_licence_number
          )
        `,
        )
        .not("applicant_id", "is", null)
        .order("created_at", { ascending: false })

      if (stageFilter !== "all") {
        query = query.eq("current_stage", stageFilter)
      }

      const { data, error } = await query

      if (error) throw error

      // Filter out applications without driving licence numbers
      const appsWithLicence = (data || []).filter((app: Record<string, unknown>) => {
        const applicant = app.applicant as {
          driving_licence_number?: string
        } | null
        return applicant?.driving_licence_number
      })

      // Fetch the latest DVLA check for each application
      const appsWithDvlaCheck = await Promise.all(
        appsWithLicence.map(async (app: Record<string, unknown>) => {
          const { data: dvlaChecks } = await supabase
            .from("driver_licence_records")
            .select("created_at")
            .eq("application_id", app.id as string)
            .order("created_at", { ascending: false })
            .limit(1)

          return {
            ...app,
            last_dvla_check: dvlaChecks?.[0] || null,
          }
        }),
      )

      return (appsWithDvlaCheck as unknown as Application[]) || []
    },
  })

  // Single recheck mutation
  const singleRecheckMutation = useMutation({
    mutationFn: async (applicationId: string) => {
      const { data, error } = await supabase.functions.invoke("dvla-recheck", {
        body: { applicationId },
      })

      if (error) throw error
      return data
    },
    onSuccess: (data, applicationId) => {
      toast.success("DVLA Check Complete", {
        description: `Successfully rechecked application ${applicationId}`,
      })
      queryClient.invalidateQueries({ queryKey: ["applications-by-stage"] })
    },
    onError: (error: Error) => {
      toast.error("Error", { description: error.message })
    },
  })

  // Batch recheck with intervals
  const batchRecheckWithInterval = async () => {
    if (selectedApplications.length === 0) {
      toast.error("No Applications Selected", {
        description: "Please select at least one application",
      })
      return
    }

    setIsProcessing(true)
    setShouldStop(false)
    setProgress(0)
    setResults([])

    const newResults: RecheckResult[] = []

    for (let i = 0; i < selectedApplications.length; i++) {
      // Check if stop was requested
      if (shouldStop) {
        toast.success("Processing Stopped", {
          description: `Stopped after ${i} of ${selectedApplications.length} applications`,
        })
        break
      }

      const appId = selectedApplications[i]

      try {
        const { data, error } = await supabase.functions.invoke("dvla-recheck", {
          body: { applicationId: appId },
        })

        if (error) throw error

        newResults.push({
          applicationId: appId,
          success: data.success,
        })

        toast.success("Progress", {
          description: `Completed ${i + 1} of ${selectedApplications.length}`,
        })
      } catch (error) {
        newResults.push({
          applicationId: appId,
          success: false,
          error: error instanceof Error ? error.message : "Unknown error",
        })
      }

      setResults([...newResults])
      setProgress(((i + 1) / selectedApplications.length) * 100)

      // Wait for interval before next request (except for last one)
      if (i < selectedApplications.length - 1 && !shouldStop) {
        await new Promise((resolve) => setTimeout(resolve, intervalSeconds * 1000))
      }
    }

    setIsProcessing(false)
    setShouldStop(false)
    queryClient.invalidateQueries({ queryKey: ["onboarded-applications"] })

    if (!shouldStop) {
      toast.success("Batch Process Complete", {
        description: `Processed ${selectedApplications.length} applications`,
      })
    }
  }

  const toggleApplication = (appId: string) => {
    setSelectedApplications((prev) =>
      prev.includes(appId) ? prev.filter((id) => id !== appId) : [...prev, appId],
    )
  }

  const toggleAll = () => {
    if (selectedApplications.length === applications?.length) {
      setSelectedApplications([])
    } else {
      setSelectedApplications(applications?.map((app) => app.id) || [])
    }
  }

  const stopProcessing = () => {
    setShouldStop(true)
    toast.success("Stopping...", {
      description: "Processing will stop after the current application",
    })
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">DVLA Recheck</h1>
            <p className="text-muted-foreground">Rerun DVLA checks for onboarded applications</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Batch Processing Options</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="stage-filter">Filter by Stage</Label>
                <Select value={stageFilter} onValueChange={setStageFilter}>
                  <SelectTrigger id="stage-filter">
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Stages</SelectItem>
                    {stagesConfig.map((stage) => (
                      <SelectItem key={stage.id} value={stage.id}>
                        {stage.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="interval">Interval (seconds)</Label>
                <Input
                  id="interval"
                  type="number"
                  min="1"
                  max="60"
                  value={intervalSeconds}
                  onChange={(e) => setIntervalSeconds(Number(e.target.value))}
                  disabled={isProcessing}
                />
              </div>

              <div className="space-y-2">
                <Label>Selected Applications</Label>
                <p className="text-2xl font-bold">
                  {selectedApplications.length} / {applications?.length || 0}
                </p>
              </div>
            </div>

            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={batchRecheckWithInterval}
                disabled={isProcessing || selectedApplications.length === 0}
              >
                {isProcessing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <PlayCircle className="mr-2 h-4 w-4" />
                )}
                Start Processing
              </Button>

              {isProcessing && (
                <Button onClick={stopProcessing} variant="destructive">
                  <StopCircle className="mr-2 h-4 w-4" />
                  Stop Processing
                </Button>
              )}
            </div>

            {isProcessing && (
              <div className="space-y-2">
                <Label>Progress</Label>
                <Progress value={progress} className="w-full" />
                <p className="text-sm text-muted-foreground">{Math.round(progress)}% complete</p>
              </div>
            )}

            {results.length > 0 && (
              <div className="space-y-2">
                <Label>Results</Label>
                <div className="border rounded-md p-4 max-h-40 overflow-y-auto">
                  {results.map((result) => (
                    <div
                      key={result.applicationId}
                      className="flex items-center justify-between py-1"
                    >
                      <span className="text-sm">{result.applicationId}</span>
                      <Badge variant={result.success ? "default" : "destructive"}>
                        {result.success ? "Success" : "Failed"}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Applications</CardTitle>
              <Button variant="outline" size="sm" onClick={toggleAll} disabled={isProcessing}>
                {selectedApplications.length === applications?.length
                  ? "Deselect All"
                  : "Select All"}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedApplications.length === applications?.length}
                        onCheckedChange={toggleAll}
                        disabled={isProcessing}
                      />
                    </TableHead>
                    <TableHead>Application ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Licence Number</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Last DVLA Check</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applications?.map((app) => (
                    <TableRow key={app.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedApplications.includes(app.id)}
                          onCheckedChange={() => toggleApplication(app.id)}
                          disabled={isProcessing}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-xs">{app.application_id}</TableCell>
                      <TableCell>
                        {app.applicant.first_name} {app.applicant.last_name}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {app.applicant.driving_licence_number || "-"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{app.status}</Badge>
                      </TableCell>
                      <TableCell>{new Date(app.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        {app.last_dvla_check ? (
                          <div className="text-sm">
                            <div>
                              {new Date(app.last_dvla_check.created_at).toLocaleDateString()}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {new Date(app.last_dvla_check.created_at).toLocaleTimeString()}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">Never</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => singleRecheckMutation.mutate(app.id)}
                          disabled={isProcessing || singleRecheckMutation.isPending}
                        >
                          {singleRecheckMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}

export default DVLARecheck
